import { CONTACT_VISIBILITY_LABEL } from "../constants/permissions";

import {
  CONTACT_VISIBILITY,
  type ContactVisibility,
  type Role,
  type RoleGrant,
  type RoleScope,
} from "@/types/role";
import {
  WORKSPACE_ROLE,
  type WorkspaceRole,
  type WorkspaceUser,
} from "@/types/user";
import { listWithMore } from "@/utils/string";

// A member without a custom role holds the built-in role of their base.
export const getMemberRole = (
  roles: Role[],
  member: { role: WorkspaceRole; roleId?: string | null },
): Role | undefined =>
  roles.find((role) => role.id === member.roleId) ??
  roles.find((role) => role.isSystem && role.base === member.role);

export const getRoleHolders = (
  roles: Role[],
  members: WorkspaceUser[],
  roleId: string | undefined,
) =>
  roleId
    ? members.filter((member) => getMemberRole(roles, member)?.id === roleId)
    : [];

// "Sita, Kiran and 2 more", by first name.
export const getHoldersLine = (names: string[]) =>
  names.length
    ? listWithMore(
        names.map((name) => name.split(" ")[0]),
        2,
      )
    : "Nobody yet";

// The built-in Admin can't be changed, copied or deleted.
export const isLockedRole = (role: Pick<Role, "isSystem" | "base">) =>
  role.isSystem && role.base === WORKSPACE_ROLE.ADMIN;

// A Member or Agent role can keep someone to some contacts; Admin, Marketing and Finance see them all.
export const canLimitContacts = (base: WorkspaceRole) =>
  base === WORKSPACE_ROLE.AGENT || base === WORKSPACE_ROLE.MEMBER;

export const seesAllContacts = (role: RoleScope) =>
  !canLimitContacts(role.base) ||
  role.contactVisibility === CONTACT_VISIBILITY.ALL;

export const getContactAccessLabel = (role: RoleScope) =>
  seesAllContacts(role)
    ? CONTACT_VISIBILITY_LABEL.all
    : CONTACT_VISIBILITY_LABEL[role.contactVisibility];

// The second line under a role in a picker.
export const getRolePickerHint = (role: RoleScope) =>
  role.base === WORKSPACE_ROLE.ADMIN
    ? "Everything, including roles and members"
    : CONTACT_VISIBILITY_LABEL[role.contactVisibility];

// A role that sees its teams' contacts, held by someone in no team, sees only
// what is assigned to them.
export const isTeamlessForRole = (role: RoleScope, teamCount: number) =>
  !seesAllContacts(role) &&
  role.contactVisibility === CONTACT_VISIBILITY.TEAMS &&
  teamCount === 0;

export const getTeamsHint = (role: RoleScope, teamCount: number) => {
  if (role.base === WORKSPACE_ROLE.ADMIN)
    return "Optional. Admins see every contact.";
  if (role.contactVisibility === CONTACT_VISIBILITY.TEAMS)
    return teamCount
      ? "They will see contacts assigned to these teams, and to them."
      : "This role sees its teams’ contacts, so pick at least one.";
  if (role.contactVisibility === CONTACT_VISIBILITY.ASSIGNED)
    return "Optional. They still see only contacts assigned to them.";

  return "Optional. Teams decide routing here, not what they see.";
};

const CONTACT_VISIBILITY_REACH: Record<ContactVisibility, number> = {
  [CONTACT_VISIBILITY.ASSIGNED]: 0,
  [CONTACT_VISIBILITY.TEAMS]: 1,
  [CONTACT_VISIBILITY.ALL]: 2,
};

export const isWiderVisibility = (
  next: ContactVisibility,
  start: ContactVisibility,
) => CONTACT_VISIBILITY_REACH[next] > CONTACT_VISIBILITY_REACH[start];

export const coversVisibility = (
  viewer: RoleGrant | null,
  contactVisibility: ContactVisibility,
) =>
  viewer?.base === WORKSPACE_ROLE.ADMIN ||
  (viewer !== null &&
    CONTACT_VISIBILITY_REACH[contactVisibility] <=
      CONTACT_VISIBILITY_REACH[viewer.contactVisibility]);

// Mirrors the backend's `Access.covers`: someone can give out a role, or change
// someone on it, only when their own role holds all of it. Admin is Admins' to give.
export const coversRole = (viewer: RoleGrant | null, role: RoleGrant) => {
  if (viewer?.base === WORKSPACE_ROLE.ADMIN) return true;
  if (!viewer || role.base === WORKSPACE_ROLE.ADMIN) return false;

  return (
    role.permissions.every((key) => viewer.permissions.includes(key)) &&
    coversVisibility(viewer, role.contactVisibility)
  );
};
